import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiCall from '../../../../api';
import './onboarding-email-verification.style.scss';

/**
 * Onboarding email-verification step. Brite (and manual bank entry) never
 * confirm that the user actually owns the email on file — Brite only sends it
 * to prefill the bank-connection session — so this step explicitly confirms
 * it via the same OTP contract Profile Settings already uses for an
 * unverified account (`generate-otp` type 4 → `employee/verify-new-v2`).
 *
 * Rendered inline (no overlay), matching the rest of this wizard.
 */
interface OnboardingEmailVerificationProps {
  email: string;
  onSuccess: () => void;
}

const OTP_LENGTH = 4;
const RESEND_SECONDS = 300;

const OnboardingEmailVerification: React.FC<OnboardingEmailVerificationProps> = ({ email, onSuccess }) => {
  const { t } = useTranslation();
  const [otpField, setOtpField] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [counter, setCounter] = useState(RESEND_SECONDS);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef<any[]>([]);

  const generateOtp = useCallback(async () => {
    if (!email) return;
    setSending(true);
    try {
      const language = localStorage.getItem('i18nextLng') || 'en';
      const result = await apiCall.post('/settings/generate-otp', { type: 4, email, lang: language });
      if (result?.status === 200) {
        setCounter(RESEND_SECONDS);
        setOtpField(new Array(OTP_LENGTH).fill(''));
        toast.success(String(t('OTP_SENT_TO_EMAIL') || 'OTP sent to your email'));
      }
    } catch (error: any) {
      toast.error(String(t(error?.response?.data?.message) || 'Failed to generate OTP'));
    } finally {
      setSending(false);
    }
  }, [email, t]);

  // Fire the first OTP as soon as the step is reached — the email is already
  // on file from the profile step, no separate "enter email" screen needed.
  useEffect(() => {
    generateOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (counter <= 0) return;
    const id = setInterval(() => setCounter((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [counter]);

  const verifyOtp = useCallback(async (otp: string) => {
    setVerifying(true);
    try {
      const result = await apiCall.put(`/employee/verify-new-v2?code=${otp}&email=${encodeURIComponent(email)}`);
      if (result?.status === 200) {
        toast.success(String(t('EMAIL_VERIFIED_SUCCESSFULLY') || 'Email verified successfully'));
        onSuccess();
      }
    } catch (error: any) {
      toast.error(String(t(error?.response?.data?.message) || 'Failed to verify OTP'));
      setOtpField(new Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [email, onSuccess, t]);

  const handleChangeOTP = useCallback((index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9a-zA-Z]/g, '').slice(-1);
    const newOtp = [...otpField];
    newOtp[index] = value;
    setOtpField(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (value && newOtp.every((digit) => digit)) {
      inputRefs.current[index]?.blur();
      verifyOtp(newOtp.join(''));
    }
  }, [otpField, verifyOtp]);

  const handleKeyDownOTP = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpField[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="onboarding-email-verify">
      <div className="onboarding-email-verify__icon" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h3 className="onboarding-email-verify__title">{t('ONBOARDING_EMAIL_VERIFY_TITLE') || 'Confirm your email'}</h3>
      <p className="onboarding-email-verify__desc">
        {t('ONBOARDING_EMAIL_VERIFY_DESC') || "Bank connection doesn't confirm email ownership, so we need to verify it separately."}
      </p>
      <p className="onboarding-email-verify__email">{email}</p>
      <p className="onboarding-email-verify__hint">
        {t('ENTER_OTP_FROM_EMAIL') || "Please check your email inbox for the one-time code. Once you've received it, enter it below to proceed."}
      </p>

      <div className="onboarding-email-verify__inputs">
        {otpField.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            disabled={sending || verifying}
            ref={(input) => (inputRefs.current[index] = input)}
            onChange={(e) => handleChangeOTP(index, e)}
            onKeyDown={(e) => handleKeyDownOTP(index, e)}
            className="onboarding-email-verify__input"
          />
        ))}
      </div>

      <div className="onboarding-email-verify__footer">
        {verifying ? (
          <span className="onboarding-email-verify__status">{t('VERIFYING') || 'Verifying...'}</span>
        ) : counter > 0 ? (
          <span className="onboarding-email-verify__status">
            {t('RESEND_IN') || 'Resend in'} {formatTime(counter)}
          </span>
        ) : (
          <button type="button" className="onboarding-email-verify__resend" onClick={generateOtp} disabled={sending}>
            {t('RESEND') || 'Resend'}
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardingEmailVerification;
